import { UploadIcon } from 'lucide-react';
import * as yup from 'yup';

import { ImportGuide } from '../../../components/pages/backoffice/import-guide.jsx';
import CancelButton from '../../../components/buttons/cancel-button.jsx';
import GoBackButton from '../../../components/buttons/go-back-button.jsx';
import { DataTableColumnHeader } from '../../../components/table/data-table-column-header.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Combobox } from '../../../components/ui/combobox.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import { Input } from '../../../components/ui/input.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.jsx';
import { useImportForm } from '../../../hooks/use-import-form.jsx';

const TABLE_COLUMNS = [
  {
    required: false,
    title: 'First Name',
    accessorKey: 'firstName',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="First Name" />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">{row.getValue('firstName')}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Last Name',
    accessorKey: 'lastName',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Last Name" />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">{row.getValue('lastName')}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Full Name',
    accessorKey: 'fullName',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Full Name" />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">{row.getValue('fullName')}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Email',
    accessorKey: 'email',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    cell: ({ row }) => <div className="lg:w-64">{row.getValue('email')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Consent Expiry Date',
    accessorKey: 'consentExpiryDate',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consent Expiry Date" />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">{row.getValue('consentExpiryDate')}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Phone',
    accessorKey: 'phone',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Phone" />
    ),
    cell: ({ row }) => <div className="lg:w-64">{row.getValue('phone')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Address',
    accessorKey: 'address',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Address" />
    ),
    cell: ({ row }) => <div className="lg:w-64">{row.getValue('address')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Zip Code',
    accessorKey: 'zipCode',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Zip Code" />
    ),
    cell: ({ row }) => <div className="lg:w-64">{row.getValue('zipCode')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Location',
    accessorKey: 'location',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Location" />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">{row.getValue('location')}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Tax ID',
    accessorKey: 'cc',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tax ID" />
    ),
    cell: ({ row }) => <div className="lg:w-64">{row.getValue('cc')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Birth Date',
    accessorKey: 'birthDate',
    dataType: 'text',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Birth Date" />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">{row.getValue('birthDate')}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Consent Terms Of Participation',
    accessorKey: 'consentTermsOfParticipation',
    dataType: 'boolean',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Consent Terms Of Participation"
      />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">
        {row.getValue('consentTermsOfParticipation')}
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    required: false,
    title: 'Consent Data Sharing',
    accessorKey: 'consentDataSharing',
    dataType: 'boolean',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consent Data Sharing" />
    ),
    cell: ({ row }) => (
      <div className="lg:w-64">{row.getValue('consentDataSharing')}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
];

const validationSchema = yup.object().shape({
  file: yup
    .mixed()
    .test(
      'required',
      'O Ficheiro é um campo obrigatório',
      (file) => file && file.size,
    )
    .test(
      'fileType',
      'O ficheiro inserido não é um ficheiro CSV ou XLXS',
      (value) => {
        return (
          (value && value.name.toLowerCase().split('.').pop() === 'csv') ||
          value.name.toLowerCase().split('.').pop() === 'xlsx'
        );
      },
    ),
  deleteCurrentRecords: yup.boolean().required(),
});

export function CustomersImport() {
  const { form, data, mapping, loadFile, onMappingFieldsChange, onFormSubmit } =
    useImportForm({
      importationId: 'customers',
      initialValues: {
        file: '',
        deleteCurrentRecords: false,
      },
      validationSchema,
      apiEndpoint: 'customers/import',
      //   onSuccessRedirect: '/backoffice/customers',
      tableColumns: TABLE_COLUMNS,
    });

  // 10854

  return (
    <>
      <GoBackButton />
      <div className="pb-6">
        <Heading className="pb-2">Importar Clientes</Heading>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFormSubmit)}>
          <ImportGuide />
          <div className="flex flex-col space-y-10 pt-10">
            <FormCard className="xl:grid-cols-3">
              <div className="col-span-1 lg:col-span-2 xl:col-span-3">
                <FormField
                  control={form.control}
                  name="file"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Ficheiro</FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          onChange={(ev) => {
                            const files = ev.target.files;
                            if (files.length > 0) loadFile(files[0]);
                            form.setValue('file', files[0]);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {TABLE_COLUMNS.map((item, index) => (
                <FormItem key={index}>
                  <FormLabel required={item.required}>{item.title}</FormLabel>
                  <Combobox
                    items={mapping.headers}
                    value={mapping?.values?.[item.accessorKey]}
                    valueKey="value"
                    labelKey="label"
                    disabled={!form.watch('file')}
                    onChange={(value) =>
                      onMappingFieldsChange(value, item.accessorKey)
                    }
                  />
                </FormItem>
              ))}
              {form.watch('file') ? (
                <>
                  <hr className="col-span-1 mx-auto my-3 h-0.5 w-3/4 border-t-0 bg-neutral-100 dark:bg-white/10 lg:col-span-2 xl:col-span-3" />
                  <div className="col-span-1 flex flex-col lg:col-span-2 xl:col-span-3">
                    <p className="pb-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed">
                      Pré-visualização dos dados
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {TABLE_COLUMNS.map((column) => (
                            <TableHead key={column.accessorKey}>
                              {column.title}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.slice(0, 10).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={TABLE_COLUMNS.length}>
                              <div className="flex items-center justify-center py-4">
                                <p>Não foi possível ler os dados.</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          data.slice(0, 10).map((item, index) => (
                            <TableRow key={index}>
                              {TABLE_COLUMNS.map((column) => (
                                <TableCell key={column.accessorKey}>
                                  {item[column.accessorKey]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : null}
            </FormCard>
            <FormFooter>
              <CancelButton />
              <Button type="submit">
                <UploadIcon />
                Importar
              </Button>
            </FormFooter>
          </div>
        </form>
      </Form>
    </>
  );
}
